import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { ArrowLeft, Edit01, CheckCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { WorkflowViewer } from '@/components/application/workflow/workflow-viewer';
import { workflowService } from '@/data/mock-workflows';
import type { Workflow } from '@/types/workflow';
import { useTranslation } from 'react-i18next';

export const WorkflowDetailPage = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const [workflow, setWorkflow] = useState<Workflow | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const edgesDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const savedTimerRef = useRef<NodeJS.Timeout | null>(null);
    const saveToLocalStorageRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (workflowId) {
            loadWorkflow();
        }

        // Cleanup timers on unmount
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (edgesDebounceTimerRef.current) {
                clearTimeout(edgesDebounceTimerRef.current);
            }
            if (savedTimerRef.current) {
                clearTimeout(savedTimerRef.current);
            }
        };
    }, [workflowId]);

    const loadWorkflow = () => {
        setIsLoading(true);
        const found = workflowService.getById(workflowId!);

        if (found) {
            // Try to load saved state from localStorage
            const flowKey = `workflow-${workflowId}`;
            const savedFlow = localStorage.getItem(flowKey);

            if (savedFlow) {
                try {
                    const parsed = JSON.parse(savedFlow);
                    // Update node positions from saved flow
                    if (parsed.nodes && Array.isArray(parsed.nodes)) {
                        const updatedNodes = found.nodes.map((node) => {
                            const savedNode = parsed.nodes.find((n: any) => n.id === node.id);
                            if (savedNode && savedNode.position) {
                                return {
                                    ...node,
                                    position: savedNode.position,
                                };
                            }
                            return node;
                        });

                        // Update edge handles from saved flow
                        let updatedEdges = found.edges;
                        if (parsed.edges && Array.isArray(parsed.edges)) {
                            updatedEdges = found.edges.map((edge) => {
                                const savedEdge = parsed.edges.find((e: any) => e.id === edge.id);
                                if (savedEdge) {
                                    return {
                                        ...edge,
                                        sourceHandle: savedEdge.sourceHandle ?? edge.sourceHandle,
                                        targetHandle: savedEdge.targetHandle ?? edge.targetHandle,
                                    };
                                }
                                return edge;
                            });
                        }

                        // Update workflow with saved positions
                        const updatedWorkflow: Workflow = {
                            ...found,
                            nodes: updatedNodes,
                            edges: updatedEdges,
                        };
                        setWorkflow(updatedWorkflow);
                    } else {
                        setWorkflow(found);
                    }
                } catch (error) {
                    console.error('Error loading saved workflow state:', error);
                    setWorkflow(found);
                }
            } else {
                setWorkflow(found);
            }
        } else {
            setWorkflow(null);
        }

        setIsLoading(false);
    };

    const handleNodesPositionChange = useCallback((updatedNodes: Workflow['nodes']) => {
        if (!workflow) return;

        // Update status to 'saving'
        setSaveStatus('saving');

        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Clear saved timer if active
        if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
        }

        // New timer with 1 second debounce
        debounceTimerRef.current = setTimeout(() => {
            workflowService.updateNodePositions(workflow.workflow_id, updatedNodes);

            // Save to localStorage
            if (saveToLocalStorageRef.current) {
                saveToLocalStorageRef.current();
            }

            setSaveStatus('saved');

            // Reset to 'idle' after 2 seconds
            savedTimerRef.current = setTimeout(() => {
                setSaveStatus('idle');
            }, 2000);
        }, 1000);
    }, [workflow]);

    const handleEdgesChange = useCallback((updatedEdges: Workflow['edges']) => {
        if (!workflow) return;

        // Update status to 'saving'
        setSaveStatus('saving');

        // Clear previous timer
        if (edgesDebounceTimerRef.current) {
            clearTimeout(edgesDebounceTimerRef.current);
        }

        // Clear saved timer if active
        if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
        }

        // New timer with 1 second debounce
        edgesDebounceTimerRef.current = setTimeout(() => {
            workflowService.updateEdges(workflow.workflow_id, updatedEdges);

            // Save to localStorage
            if (saveToLocalStorageRef.current) {
                saveToLocalStorageRef.current();
            }

            setSaveStatus('saved');

            // Reset to 'idle' after 2 seconds
            savedTimerRef.current = setTimeout(() => {
                setSaveStatus('idle');
            }, 2000);
        }, 1000);
    }, [workflow]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    if (!workflow) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-primary mb-2">{t('workflows.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('workflows.notFound.message')}</p>
                <Button onClick={() => navigate('/admin/workflows')} size="md" color="primary">
                    {t('workflows.notFound.backToList')}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-120px)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-secondary">
                <div className="flex items-center gap-4">
                    <Link to="/admin/workflows">
                        <Button size="sm" color="secondary" iconLeading={ArrowLeft}>
                            {t('workflows.detail.back')}
                        </Button>
                    </Link>
                    <h2 className="text-xl font-semibold text-primary">{workflow.name}</h2>
                </div>
                {!workflow.is_default && (
                    <Button
                        onClick={() => navigate(`/admin/workflows/${workflow.workflow_id}/edit`)}
                        size="md"
                        color="primary"
                        iconLeading={Edit01}
                    >
                        {t('workflows.detail.edit')}
                    </Button>
                )}
            </div>

            {/* Workflow Viewer - Full Page */}
            <div className="flex-1 overflow-hidden relative">
                {/* Save Status Indicator */}
                {saveStatus !== 'idle' && (
                    <div className="absolute top-4 right-4 z-10 flex items-center gap-2 rounded-lg bg-primary px-3 py-2 shadow-lg ring-1 ring-secondary">
                        {saveStatus === 'saving' ? (
                            <>
                                <div className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                                <span className="text-sm text-primary">{t('workflows.savingChanges')}</span>
                            </>
                        ) : (
                            <>
                                <CheckCircle className="size-4 text-utility-success-600" />
                                <span className="text-sm text-primary">{t('workflows.allSaved')}</span>
                            </>
                        )}
                    </div>
                )}
                <WorkflowViewer
                    nodes={workflow.nodes}
                    edges={workflow.edges}
                    workflowId={workflow.workflow_id}
                    onNodesPositionChange={handleNodesPositionChange}
                    onEdgesChange={handleEdgesChange}
                    onSaveReady={(onSave) => {
                        saveToLocalStorageRef.current = onSave;
                    }}
                />
            </div>
        </div>
    );
};
